import { getWellSkyClientData } from "../api/integrationApi";

function asText(value) {
  if (value == null) return "";
  return String(value).trim();
}

function splitConditions(text) {
  const raw = asText(text);
  if (!raw) return [];
  return raw
    .split(/[\n;,|]+/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function formatContact(contact) {
  if (!contact) return "";
  const name = [contact.firstName, contact.lastName].filter(Boolean).join(" ").trim();
  const parts = [
    name,
    contact.relationship ? `(${contact.relationship})` : "",
    contact.phone || contact.mobile || "",
    contact.email || "",
  ].filter(Boolean);
  return parts.join(" · ");
}

async function fetchWellSkyResource(resourceType, wellSkyId) {
  try {
    const res = await getWellSkyClientData(resourceType, wellSkyId);
    return res?.data || {};
  } catch (err) {
    if (err?.response?.status === 404) return {};
    throw err;
  }
}

/**
 * Map synced WellSky payloads into Client Snapshot aipAnalysisData fields.
 * Only returns values that can be sourced from WellSky sync records.
 */
export async function loadWellSkySnapshotPatch(wellSkyId) {
  if (!wellSkyId) return {};

  const [patient, condition, medication, careplan, relatedperson] =
    await Promise.all([
      fetchWellSkyResource("patient", wellSkyId),
      fetchWellSkyResource("condition", wellSkyId),
      fetchWellSkyResource("medication", wellSkyId),
      fetchWellSkyResource("careplan", wellSkyId),
      fetchWellSkyResource("relatedperson", wellSkyId),
    ]);

  const patch = {};

  if (asText(patient.email)) patch.email = patient.email;
  if (asText(patient.language)) patch.primaryLanguage = patient.language;
  if (asText(patient.livesWith)) patch.householdComposition = patient.livesWith;
  if (asText(patient.locationName)) patch.branchOfficeLocation = patient.locationName;
  if (asText(patient.community)) patch.referralSource = patient.community;
  if (asText(patient.createdAt)) patch.referralDate = patient.createdAt.slice(0, 10);
  if (asText(patient.notes)) patch.caseNotes = patient.notes;
  if (asText(patient.weight)) patch.weight = patient.weight;
  if (asText(patient.dnr)) {
    patch.advanceDirectives = `DNR: ${patient.dnr}`;
    patch.dnrOnFile = /yes|true/i.test(String(patient.dnr)) ? "Yes" : "No";
  }

  const chronic = asText(condition?.medicalConditions?.chronicConditions);
  if (chronic) {
    patch.medicalHistorySummary = chronic;
    splitConditions(chronic).slice(0, 4).forEach((entry, index) => {
      patch[`diagnosedCondition${index + 1}`] = entry;
    });
  }

  const functionalLimitations = condition?.medicalConditions?.functionalLimitations;
  if (functionalLimitations && typeof functionalLimitations === "object") {
    const active = Object.entries(functionalLimitations)
      .filter(([, value]) => asText(value))
      .map(([key, value]) => `${key}: ${value}`);
    if (active.length) {
      patch.functionalLimitations = active.join("; ");
    }
  }

  const meds = Array.isArray(medication?.medications) ? medication.medications : [];
  meds.slice(0, 5).forEach((med, index) => {
    const label = [med.name, med.form, med.strength, med.dosage]
      .filter(Boolean)
      .join(" — ");
    if (label) patch[`prescribedMedication${index + 1}`] = label;
  });

  const activities = Array.isArray(careplan?.activities) ? careplan.activities : [];
  const serviceNames = activities
    .map((row) => asText(row.activityName || row.name))
    .filter(Boolean);
  if (serviceNames.length) {
    patch.servicesOrdered = serviceNames.join(", ");
  }

  const adlNeeds = Array.isArray(careplan?.adl) ? careplan.adl : [];
  if (adlNeeds.length) {
    patch.careLevelAcuity = adlNeeds
      .map((row) => `${row.name}: ${row.levelOfNeed || row.score || "—"}`)
      .join("; ");
  }

  const contacts = Array.isArray(relatedperson?.contacts) ? relatedperson.contacts : [];
  const primary =
    contacts.find((row) => row.primaryContact) ||
    contacts.find((row) => row.emergencyContact) ||
    contacts[0];
  if (primary) {
    patch.primaryCaregiverDetails = formatContact(primary);
  }

  return patch;
}

export function mergeWellSkySnapshotPatch(existing = {}, patch = {}) {
  const next = { ...existing };
  Object.entries(patch).forEach(([key, value]) => {
    const incoming = asText(value);
    if (!incoming) return;
    const current = asText(next[key]);
    if (!current) next[key] = incoming;
  });
  return next;
}
